import { useState, useCallback, useEffect } from 'react';
import { api } from '../utils/api';
import { useConfirmStore } from '../store/confirmStore';

export interface Invite {
  id: number;
  code?: string;
  code_hash: string;
  role: string;
  is_used: boolean;
  created_at?: string;
  expires_at?: string;
  used_count: number;
  max_uses: number;
  is_active: boolean;
}

export function useAdminInvites() {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [creating, setCreating] = useState(false);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/api/auth/invites');
      setInvites(r.data);
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  const createInvite = async (role: string, ttl: number | null, maxUses: number) => {
    setCreating(true);
    setCreatedCode(null);
    setError(null);
    try {
      const res = await api.post('/api/auth/invite', {
        role,
        expires_in_hours: ttl,
        max_uses: maxUses,
      });
      setCreatedCode(res.data.code);
      await loadInvites();
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  const deleteInvite = async (id: number) => {
    const confirmed = await useConfirmStore.getState().confirm({
      title: 'Удаление инвайт-кода',
      message: 'Удалить инвайт-код?',
      confirmText: 'Удалить',
      cancelText: 'Отмена'
    });
    if (!confirmed) return;
    try {
      await api.delete(`/api/auth/invites/${id}`);
      setInvites(prev => prev.filter(i => i.id !== id));
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return {
    invites,
    loading,
    error,
    creating,
    createdCode,
    copied,
    createInvite,
    deleteInvite,
    copyCode,
    refresh: loadInvites
  };
}
